import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { css } from "@codemirror/lang-css";
import { useMemo } from "react";
import type { RepoFile } from "../lib/types";
import { I } from "./Icons";

interface Props {
  files: Record<string, RepoFile>;
  activeFile: string;
  onActiveFileChange: (name: string) => void;
  onContentChange: (name: string, content: string) => void;
  readOnly?: boolean;
}

function langFor(name: string) {
  if (name.endsWith(".css")) return css();
  return javascript({ jsx: true });
}

export function CodeEditor({
  files,
  activeFile,
  onActiveFileChange,
  onContentChange,
  readOnly,
}: Props) {
  const fileNames = Object.keys(files);
  const active = files[activeFile];

  const extensions = useMemo(() => [langFor(activeFile)], [activeFile]);

  return (
    <div className="gf-editor">
      <div className="gf-tree">
        <div className="gf-tree__head">my-app</div>
        {fileNames.map((name) => {
          const f = files[name];
          const cls = ["gf-tree__item"];
          if (name === activeFile) cls.push("is-active");
          if (f.status === "modified") cls.push("gf-tree__item--modified");
          if (f.status === "staged") cls.push("gf-tree__item--staged");
          if (f.status === "untracked")
            cls.push("gf-tree__item--untracked");
          if (f.status === "conflicted")
            cls.push("gf-tree__item--conflicted");
          return (
            <div
              key={name}
              className={cls.join(" ")}
              onClick={() => onActiveFileChange(name)}
              title={f.status}
            >
              <I.FileCode size={14} />
              <span>{name}</span>
              <span className="gf-tree__status"></span>
            </div>
          );
        })}
        <div
          style={{
            marginTop: 12,
            padding: "8px 10px",
            fontSize: 11,
            color: "var(--fg-3)",
            lineHeight: 1.5,
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <span
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--warning)",
                marginRight: 6,
              }}
            ></span>
            modified
          </div>
          <div style={{ marginBottom: 4 }}>
            <span
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--success)",
                marginRight: 6,
              }}
            ></span>
            staged
          </div>
          <div>
            <span
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--fg-link)",
                marginRight: 6,
              }}
            ></span>
            untracked
          </div>
        </div>
      </div>
      <div className="gf-editor__pane">
        <div className="gf-editor__tabs">
          {fileNames.map((name) => {
            const status = files[name].status;
            const cls = ["gf-editor__tab"];
            if (name === activeFile) cls.push("is-active");
            if (status === "modified") cls.push("gf-editor__tab--dirty", "gf-editor__tab--modified");
            if (status === "staged") cls.push("gf-editor__tab--staged");
            if (status === "conflicted") cls.push("gf-editor__tab--conflicted");
            return (
              <button
                key={name}
                className={cls.join(" ")}
                onClick={() => onActiveFileChange(name)}
              >
                {name}
              </button>
            );
          })}
        </div>
        <div className="gf-editor__area">
          <CodeMirror
            value={active.content}
            extensions={extensions}
            onChange={(v) => onContentChange(activeFile, v)}
            readOnly={readOnly}
            theme="dark"
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              highlightActiveLine: true,
              indentOnInput: true,
            }}
            height="100%"
            style={{ height: "100%", fontSize: 13 }}
          />
        </div>
      </div>
    </div>
  );
}
